import { createFileRoute } from '@tanstack/react-router';
import { useUser } from '@clerk/clerk-react';
import { useUserOrganizations } from '@/hooks/useOrganization';
import { useNavigate } from '@tanstack/react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader } from '@/components/ui/loader';
import { PlusIcon, BuildingIcon, ArrowRightIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from '@/hooks/useTranslation';
import { CreateOrganizationForm } from '@/components/organization/CreateOrganizationForm';
import type { ApiOrganization } from '@envase/api/client';

export const Route = createFileRoute('/_authenticated/dashboard')({
  component: Dashboard,
});

function Dashboard() {
  const { user } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { organizations, isLoading } = useUserOrganizations();
  
  const orgList: ApiOrganization[] = organizations || [];
  const hasOrganizations = orgList.length > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">
          {user?.firstName ? t('dashboard.welcomeBack', { name: user.firstName }) : t('dashboard.welcomeBackDefault')}
        </h1>
        <p className="text-muted-foreground mt-2">
          {t('dashboard.selectOrganization')}
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader variant="spinner" size="lg" color="primary" text={t('dashboard.loadingDashboard')} />
        </div>
      ) : hasOrganizations ? (
        <>
          {/* Organization Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {orgList.map((org: ApiOrganization) => (
              <Card 
                key={org.id}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate({ to: `/org/${org.id}` })}
                data-testid={`organization-card-${org.slug}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <BuildingIcon className="w-8 h-8 text-primary" />
                    <Badge 
                      variant={org.role === 'owner' ? 'default' : 'secondary'}
                      className="text-xs"
                    >
                      {t(`organization.roles.${org.role}`)}
                    </Badge>
                  </div>
                  <CardTitle className="mt-4">{org.name}</CardTitle>
                  <CardDescription>@{org.slug}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <span>{t('dashboard.openOrganization')}</span>
                    <ArrowRightIcon className="w-4 h-4 ml-2" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Create Organization Action */}
          <div className="flex justify-center">
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => navigate({ to: '/create-organization' })}
              className="gap-2"
              data-testid="create-organization-button"
            >
              <PlusIcon className="w-5 h-5" />
              {t('dashboard.createNewOrganization')}
            </Button>
          </div>
        </>
      ) : (
        // Empty state - no organizations - show form directly
        <div className="flex flex-col items-center justify-center py-12">
          <BuildingIcon className="w-16 h-16 text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">{t('dashboard.noOrganizations')}</h2>
          <p className="text-muted-foreground mb-8 text-center max-w-md">
            {t('dashboard.noOrganizationsDescription')}
          </p>
          <div className="w-full max-w-2xl">
            <CreateOrganizationForm
              onSuccess={(org) => {
                navigate({ to: `/org/${org.id}` });
              }}
              showTitle={false}
              showDescription={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}

